import { useState } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@finn-auto/design-system/atoms/select';
import { Card, GroupTag, Kpi } from './ui';

/**
 * The Dashboard in four sections, as mockup v0.5 and design v0.6 §7 define it. Every number is
 * "—" until there is real data (Lever, the tool's own click tracking, consent and deletion
 * records); each tile and chart says what it will measure, so the definitions can be argued
 * with now. No opens anywhere: Gmail gives no reliable opens.
 */
const PERIODS = { '12m': 'last 12 months', '6m': 'last 6 months', '90d': 'last 90 days' } as const;
type Period = keyof typeof PERIODS;

function ChartCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Card title={title}>
      <div className="flex h-36 items-center justify-center rounded border border-dashed border-zinc-300 text-sm text-zinc-400">
        No data yet
      </div>
      <p className="mt-2 text-xs text-zinc-500">{children}</p>
    </Card>
  );
}

function Section({ n, title, children }: { n: number; title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-3">
      <h2 className="text-xs font-semibold uppercase tracking-wide text-zinc-500">
        {n} · {title}
      </h2>
      {children}
    </section>
  );
}

export function DashboardTab() {
  const [period, setPeriod] = useState<Period>('12m');

  return (
    <div className="space-y-8">
      <p className="text-sm text-zinc-600">All figures appear once candidates are in the tool.</p>

      <Section n={1} title="Top KPIs">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
          <Kpi label="Members" tag={<GroupTag group="vetted" />} sub="new this month · pre-vetted" />
          <Kpi label="Members" tag={<GroupTag group="leads" />} sub="new this month · sourced or signed up" />
          <Kpi label="Members" tag={<GroupTag group="internal" />} sub="not in version 1" />
          <Kpi label="Hires from Talent Community" sub="this year, and share of all FINN hires" />
          <Kpi label="Net growth" sub="this month: joined minus left" />
        </div>
        <div className="grid gap-4 lg:grid-cols-2">
          <ChartCard title="Source yield — hires per candidate added">
            Hires ÷ candidates added, by source: Lever (pre-vetted), LinkedIn, careers page and events.
          </ChartCard>
          <ChartCard title="Where our talent comes from">A donut of the sources: Lever, LinkedIn, careers page and events.</ChartCard>
        </div>
      </Section>

      <Section n={2} title="Campaigns">
        <Card title="Filter">
          <div className="flex flex-wrap items-end gap-4">
            <div className="min-w-[240px] flex-1 text-sm text-zinc-700">
              <span className="mb-1 block">Campaign</span>
              <Select value="all" onValueChange={() => undefined}>
                <SelectTrigger aria-label="Campaign">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All campaigns</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="w-52 text-sm text-zinc-700">
              <span className="mb-1 block">Period</span>
              <Select value={period} onValueChange={(v: string) => setPeriod(v as Period)}>
                <SelectTrigger aria-label="Period">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(Object.keys(PERIODS) as Period[]).map((p) => (
                    <SelectItem key={p} value={p}>
                      {PERIODS[p].charAt(0).toUpperCase() + PERIODS[p].slice(1)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <p className="mt-3 text-xs text-zinc-500">
            Everyone who received a campaign in the {PERIODS[period]}. Each person is counted once, even if they received
            several campaigns.
          </p>
        </Card>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Kpi label="Entered process" sub="confirmed in the portal, opportunity created in Lever" />
          <Kpi label="Click rate" sub="clicked any link ÷ received" />
          <Kpi label="Opt-out rate" sub="opted out ÷ received" />
          <Kpi label="Expressed interest rate" sub="clicked Interested ÷ received" />
        </div>
        <ChartCard title="Campaign funnel — received a campaign to hire">
          Received a campaign → expressed interest (clicked Interested) → entered process (confirmed in the portal,
          opportunity created in Lever) → hired. Percentages are conversion from the previous step.
        </ChartCard>
      </Section>

      <Section n={3} title="Demographics">
        <div className="grid gap-4 lg:grid-cols-2">
          <ChartCard title="By seniority">All groups combined. Aggregated only; groups smaller than 5 are not shown.</ChartCard>
          <ChartCard title="By location">All groups combined. Aggregated only; groups smaller than 5 are not shown.</ChartCard>
        </div>
      </Section>

      <Section n={4} title="GDPR">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Kpi label="Scope A consented" sub="stored in the Talent Community, share of invited" />
          <Kpi label="Scope B consented" sub="also want news, share of Scope A" />
          <Kpi label="Renewals in flight" sub="renewal emails sent, no answer yet" />
          <Kpi label="Open data requests" sub="the portal covers access requests (Art. 15)" />
        </div>
        <ChartCard title="Consent funnel — last 90 days">Entered → invitation sent → consented.</ChartCard>
        <details className="rounded-lg border border-solid border-pearl bg-white p-5">
          <summary className="cursor-pointer text-sm font-semibold text-zinc-900">Compliance details — legal basis per group</summary>
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="text-xs uppercase tracking-wide text-zinc-500">
                <tr>
                  <th className="py-2 pr-4 font-medium">Group</th>
                  <th className="py-2 pr-4 font-medium">Term</th>
                  <th className="py-2 font-medium">What it means</th>
                </tr>
              </thead>
              <tbody className="text-zinc-700">
                <tr className="border-t border-solid border-zinc-100">
                  <td className="py-2 pr-4">
                    <GroupTag group="vetted" />
                  </td>
                  <td className="py-2 pr-4">24 months, renewable</td>
                  <td className="py-2">Consent after archive. Only a stub is kept until they agree.</td>
                </tr>
                <tr className="border-t border-solid border-zinc-100">
                  <td className="py-2 pr-4">
                    <GroupTag group="leads" />
                  </td>
                  <td className="py-2 pr-4">24 months from consent</td>
                  <td className="py-2">Consent at sign-up, or one 1:1 approach for sourced leads. Nothing is stored until they opt in.</td>
                </tr>
                <tr className="border-t border-solid border-zinc-100">
                  <td className="py-2 pr-4">
                    <GroupTag group="internal" />
                  </td>
                  <td className="py-2 pr-4">While employed</td>
                  <td className="py-2">Employment basis (BDSG §26), not consent. Opt out any time; hidden from their own manager.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </details>
      </Section>
    </div>
  );
}
